import {
  CheckCircle,
  ChevronRight,
  ErrorOutline,
  RadioButtonUnchecked,
  ScheduleOutlined,
} from '@campoint/odi-ui-icons';
import { Button, Flex, Icon, Text } from '@chakra-ui/react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { clickableTaskItemStates } from '../../../section/TaskSection/TaskSection';

export type OnboardingTaskState = 'todo' | 'pending' | 'error' | 'done';

type StateColorSet = {
  /**
   * Optional color for text content active state.
   */
  textContentActive?: string;
  /**
   * Optional color for sub text content active state.
   */
  subTextContentActive?: string;
  /**
   * Optional color for text content disabled state.
   */
  textContentDisabled?: string;
  /**
   * Optional color for sub text content disabled state.
   */
  subTextContentDisabled?: string;
  /**
   * Optional color for pressed state.
   */
  pressed?: string;
};

type ColorSet = {
  stateColors?: StateColorSet;
  /**
   * Optional icon colors
   */
  iconColors?: {
    leadingIcon?: string;
    trailingIcon?: string;
  };
  /**
   * Optional border color
   */
  borderColor?: string;
};

const getListItemColorTheme = (state: OnboardingTaskState) => {
  const colorSetTodo: ColorSet = {
    iconColors: {
      leadingIcon: 'onSurface.mediumEmphasis', // theme.onSuccess.mediumEmphasis,
    },
  };
  const colorSetDone: ColorSet = {
    borderColor: 'success.highEmphasis', // theme.success.highEmphasis,
    iconColors: {
      leadingIcon: 'success.highEmphasis', // theme.success.highEmphasis,
    },
    stateColors: {
      textContentActive: 'success.highEmphasis', // theme.success.highEmphasis,
      subTextContentDisabled: 'success.highEmphasis', // theme.success.highEmphasis,
      textContentDisabled: 'success.highEmphasis', // theme.success.highEmphasis,
    },
  };
  const colorSetPending: ColorSet = {
    borderColor: 'warning.highEmphasis', // theme.warning.highEmphasis,
    iconColors: {
      leadingIcon: 'warning.highEmphasis', // theme.warning.highEmphasis,
    },
    stateColors: {
      textContentActive: 'warning.highEmphasis', // theme.warning.highEmphasis,
      textContentDisabled: 'warning.highEmphasis', // theme.warning.highEmphasis,
      subTextContentDisabled: 'warning.highEmphasis', // theme.warning.highEmphasis,
    },
  };
  const colorSetError: ColorSet = {
    borderColor: 'error.highEmphasis', // theme.error.highEmphasis,
    iconColors: {
      leadingIcon: 'error.highEmphasis', // theme.error.highEmphasis,
    },
    stateColors: {
      textContentActive: 'error.highEmphasis', // theme.error.highEmphasis,
    },
  };

  switch (state) {
    case 'todo': {
      return colorSetTodo;
    }
    case 'pending': {
      return colorSetPending;
    }
    case 'error': {
      return colorSetError;
    }
    case 'done': {
      return colorSetDone;
    }
    default: {
      return colorSetTodo;
    }
  }
};

const getListItemLeadingIcon = (state: OnboardingTaskState) => {
  switch (state) {
    case 'todo': {
      return RadioButtonUnchecked;
    }
    case 'pending': {
      return ScheduleOutlined;
    }
    case 'error': {
      return ErrorOutline;
    }
    case 'done': {
      return CheckCircle;
    }
    default: {
      return undefined;
    }
  }
};

export type HomeTaskCardItemProps = {
  category: 'account' | 'age' | 'profile' | 'modelReleaseForm';
  state: OnboardingTaskState;
  isClickable?: boolean;
  onClick?: () => void;
};

const getItemText = (
  t: TFunction<'home', 'cards.tasks'>,
  category: HomeTaskCardItemProps['category'],
  state: HomeTaskCardItemProps['state']
): string => {
  switch (category) {
    case 'account': {
      return t(`account.done`);
    }

    default: {
      return t(`${category}.${state}`);
    }
  }
};

/**
 * @param props.category allowed categories
 * @param props.state allowed states
 * @returns TaskCardItem
 */
const HomeTaskCardItem: React.FC<HomeTaskCardItemProps> = ({
  isClickable = false,
  onClick = () => {
    // initially must be empty
  },
  ...props
}) => {
  const { t } = useTranslation('home', { keyPrefix: 'cards.tasks' });

  const colorSet = getListItemColorTheme(props.state);
  const leadingIcon = getListItemLeadingIcon(props.state);
  const hasLeadingIcon =
    getListItemLeadingIcon(props.state) === undefined ? false : true;
  const trailingIcon = clickableTaskItemStates.includes(props.state)
    ? ChevronRight
    : undefined;
  const text = getItemText(t, props.category, props.state);

  const cursorHoverStyle = isClickable ? 'pointer' : 'auto';

  return (
    <>
      {trailingIcon && hasLeadingIcon ? (
        <Button
          data-testid={'HomeTaskItem'}
          variant={'ghost'}
          fontWeight={'normal'}
          w={['full', 'full', 'full', 'full', 'auto']}
          colorScheme="onSurface"
          borderColor={colorSet?.borderColor}
          borderWidth={'1px'}
          justifyContent={'space-between'}
          onClick={isClickable ? onClick : undefined}
          _hover={{ cursor: cursorHoverStyle }}
          minH={'14'}
        >
          <Flex alignItems={'center'}>
            <Icon
              as={leadingIcon}
              color={colorSet?.iconColors?.leadingIcon}
              boxSize={'icon.md'}
              mr={2}
            />
            <Text color={colorSet?.borderColor}>{text}</Text>
          </Flex>
          <Icon
            as={trailingIcon}
            boxSize={'icon.md'}
            color={colorSet?.iconColors?.leadingIcon}
          />
        </Button>
      ) : (
        <Button
          data-testid={'HomeTaskItem'}
          variant={'ghost'}
          fontWeight={'normal'}
          w={['full', 'full', 'full', 'full', 'auto']}
          borderColor={colorSet?.borderColor}
          borderWidth={'1px'}
          colorScheme="onSurface"
          justifyContent={'space-between'}
          onClick={!isClickable ? undefined : onClick}
          _hover={{ cursor: cursorHoverStyle }}
          minH={'14'}
        >
          <Flex alignItems={'center'}>
            <Icon
              as={leadingIcon}
              color={colorSet?.iconColors?.leadingIcon}
              boxSize={'icon.md'}
              mr={2}
            />
            <Text color={colorSet?.borderColor}>{text}</Text>
          </Flex>
        </Button>
      )}
    </>
  );
};

export default HomeTaskCardItem;
